import { ReactElement } from 'react';
import { Grid, Box, MenuItem, Card, CardContent, Button } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useRoutes, useAwsGetter } from '@localstack/services';

import {
  Dropdown,
  LoadingFragment,
  LambdaDetails,
  LambdaCodeDetails,
  LambdaEventSourceMappings,
  LambdaTags,
  PageTitle,
} from '@localstack/ui';

import { LambdaProps } from './types';
import { FunctionTabs } from './components/FunctionTabs';

export const LambdaFunction = ({ Layout, clientOverrides, routes }: LambdaProps): ReactElement => {
  const { goto } = useRoutes();

  const { name: functionNameWithVersion } = useParams<'name'>();
  const [functionNameWithoutVersion, functionVersion] = functionNameWithVersion?.split(':') ?? [];

  const { data: lambdaFunction, isLoading: isFunctionLoading } = useAwsGetter(
    'Lambda',
    'getFunction',
    [{ FunctionName: functionNameWithVersion }],
    { clientOverrides },
  );
  const { data: versions, isLoading: isVersionsLoading } = useAwsGetter(
    'Lambda',
    'listVersionsByFunction',
    [{ FunctionName: functionNameWithoutVersion }],
    { clientOverrides },
  );
  const { data: mappings, isLoading: isMappingsLoading } = useAwsGetter(
    'Lambda',
    'listEventSourceMappings',
    [{ FunctionName: functionNameWithVersion }],
    { clientOverrides },
  );

  const hasCapacityProvider =
    !!lambdaFunction?.Configuration?.CapacityProviderConfig?.LambdaManagedInstancesCapacityProviderConfig
      ?.CapacityProviderArn;

  return (
    <Layout
      documentTitle="Lambda: Event Function Details"
      title={
        <PageTitle
          title="Event Function Details"
          breadcrumbs={[
            ['Lambda', () => goto(routes.RESOURCES_LAMBDA)],
            [functionNameWithVersion, null],
          ]}
        />
      }
      tabs={
        <FunctionTabs
          routes={routes}
          functionName={functionNameWithVersion}
          showScalingConfigTab={hasCapacityProvider}
        />
      }
      actions={
        <>
          <Button
            variant="outlined"
            onClick={() => goto(routes.RESOURCES_LAMBDA_FUNCTION_UPDATE_CODE, { name: functionNameWithVersion })}
          >
            Update Event Function Code
          </Button>
        </>
      }
    >
      <>
        <Box textAlign="right" mb={2} display="flex" justifyContent="flex-end" alignItems="center">
          <Dropdown label={functionVersion || 'Version'} size="small" variant="text">
            {(versions?.Versions ?? []).map((v) => (
              <MenuItem
                key={v.RevisionId}
                onClick={() => goto(routes.RESOURCES_LAMBDA_FUNCTION, { name: `${v.FunctionName}:${v.Version}` })}
              >
                {v.Version}
              </MenuItem>
            ))}
          </Dropdown>
        </Box>
        <Grid container spacing={3}>
          <Grid item md={6}>
            <Card>
              <CardContent>
                <LoadingFragment loading={isFunctionLoading || isVersionsLoading} variant="list" height={50} size={6}>
                  <LambdaDetails
                    lambdaFunction={lambdaFunction?.Configuration}
                    showCapacityProviderField={hasCapacityProvider}
                  />
                </LoadingFragment>
              </CardContent>
            </Card>
          </Grid>
          <Grid item md={6}>
            <Grid container spacing={3}>
              <Grid item md={12}>
                <Card>
                  <CardContent>
                    <LoadingFragment
                      loading={isFunctionLoading || isVersionsLoading}
                      variant="list"
                      height={50}
                      size={5}
                    >
                      <LambdaCodeDetails
                        lambdaFunction={lambdaFunction?.Configuration}
                        lambdaFunctionCodeLocation={lambdaFunction?.Code}
                      />
                    </LoadingFragment>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item md={12}>
                <Card>
                  <CardContent>
                    <LoadingFragment
                      loading={isFunctionLoading || isVersionsLoading}
                      variant="list"
                      height={50}
                      size={5}
                    >
                      <LambdaTags lambdaFunction={lambdaFunction} />
                    </LoadingFragment>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>
          <Grid item md={12}>
            <Card>
              <CardContent>
                <LoadingFragment
                  loading={isMappingsLoading}
                  arrayData={mappings?.EventSourceMappings ?? []}
                  variant="list"
                  height={50}
                  size={6}
                >
                  <LambdaEventSourceMappings mappings={mappings?.EventSourceMappings ?? []} />
                </LoadingFragment>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </>
    </Layout>
  );
};
